<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { push } from 'svelte-spa-router';

	import { user_settings } from 'src/stores/account/user';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { experimental_feature_feedback_reminders } from 'src/stores/features/experimental-reminders';

	import { save_home } from 'src/helpers/home_helpers';
	import { openExternalLink } from 'src/helpers/utilities';

	import { FIVE_DAYS_MS } from 'src/constants';

	import {
		experimentalFeatures,
		toggleExperimentalFeature,
		type ExperimentalFeatureId,
	} from './experimental_feature_helpers';

	import CopySettingsOnboardingWizard from '../ExperimentalFeatures/CopySettingsOnboardingWizard.svelte';
	import NetMeteringInfoModal from 'src/components/Consumption/NetMeteringInfoModal.svelte';
	import PVSetupConfig from 'src/components/Consumption/PVSetupEnergy/PVSetupConfig.svelte';

	import { SelectCard, Toggle, Text, toast } from '@Shelly/shelly-design-system';

	export let featureId: ExperimentalFeatureId;

	const meta = experimentalFeatures[featureId];

	$: featureName = $format(meta.nameKey, { default: meta.nameDefault });
	$: featureDescription = $format(meta.descriptionKey, { default: meta.descriptionDefault });

	$: isSelected = $user_settings?.experimental_features?.[featureId]?.enabled === 1;

	// Modal state — only the modal whose featureId matches actually renders below.
	let showCloneSettings = false;
	let showNetMeteringModal = false;
	let showPVSetupModal = false;

	let reminderTimerId: number | undefined;

	const cancelReminder = (): void => {
		if (typeof window === 'undefined') return;
		if (reminderTimerId !== undefined) {
			window.clearTimeout(reminderTimerId);
			reminderTimerId = undefined;
		}
	};

	const scheduleReminder = (enabledAt: number): void => {
		cancelReminder();
		if (typeof window === 'undefined' || !meta.feedbackLink) {
			experimental_feature_feedback_reminders.clearReminder(featureId);
			return;
		}
		const remaining = Math.max(FIVE_DAYS_MS - (Date.now() - enabledAt), 0);
		const fire = (): void => {
			toast.open({
				type: 'info',
				title: $format('experimental_features_feedback_reminder_title', {
					default: 'Share your feedback',
				}),
				text: $format('experimental_features_feedback_reminder_text', {
					default:
						'It has been a few days since you enabled {name}. Join the Discord discussion to share your thoughts: {link}',
					values: { name: featureName, link: meta.feedbackLink ?? '' },
				}),
				duration: 7000,
			});
			reminderTimerId = undefined;
			experimental_feature_feedback_reminders.clearReminder(featureId);
		};
		if (remaining === 0) {
			fire();
			return;
		}
		reminderTimerId = window.setTimeout(fire, remaining);
	};

	onMount(() => {
		if (typeof window === 'undefined') return;
		const stored = get(experimental_feature_feedback_reminders)?.[featureId];
		if (!stored) return;
		if (!isSelected || !meta.feedbackLink) {
			experimental_feature_feedback_reminders.clearReminder(featureId);
			return;
		}
		scheduleReminder(stored);
	});

	onDestroy(() => {
		cancelReminder();
	});

	const handleToggle = async (event?: Event): Promise<void> => {
		event?.stopPropagation();
		const willBeEnabled = !isSelected;

		if (willBeEnabled && featureId === 'cs') showCloneSettings = true;

		const ok = await toggleExperimentalFeature(featureId, willBeEnabled);
		if (!ok) {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
			return;
		}

		if (featureId === 'nm' && willBeEnabled) {
			showNetMeteringModal = true;
		} else if (featureId === 'pvs' && willBeEnabled) {
			showPVSetupModal = true;
		}

		const hasOnboarding = ['cs', 'nm', 'pvs'].includes(featureId);
		if (willBeEnabled && !hasOnboarding) {
			toast.open({
				type: 'warning',
				title: $format('experimental_features_enabled_title', {
					default: '{name} Enabled',
					values: { name: featureName },
				}),
				text: $format('experimental_features_enabled_text', {
					default:
						'You have enabled {name}. This feature may not work correctly with all apps.',
					values: { name: featureName },
				}),
				duration: 5000,
			});
		}
	};
</script>

<div class="mt-s">
	<SelectCard
		cardBgColor={$showNewLayout ? 'color-1' : undefined}
		hideAction={true}
		variant="toggle"
		type="horizontal"
		size="s"
		class="width-full"
		icon={meta.icon}
		title={featureName}
		descriptiveText={featureDescription}
		selected={isSelected}
		on:click={(event) => handleToggle(event)}
	>
		<Toggle
			size="m"
			slot="customActionRight"
			checked={isSelected}
			on:click={(event) => handleToggle(event)}
			class="d-flex justify-content-center align-items-center"
			aria-label={$format(`toggle_${featureName}`, {
				default: 'Toggle {name}',
				values: { name: featureName },
			})}
			id={meta.id}
		/>
	</SelectCard>

	{#if meta.feedbackLink}
		<Text
			styleType="body-small"
			class="link-styles text-small mt-xs mb-s d-inline-flex align-items-center text-primary"
			on:click={() => meta.feedbackLink && openExternalLink(meta.feedbackLink)}
			target="_blank"
			rel="noopener noreferrer"
		>
			{$format('experimental_features_feedback_discord', {
				default: 'Discuss this feature on Discord',
				values: { name: featureName },
			})}
		</Text>
	{/if}
</div>

{#if featureId === 'nm'}
	<NetMeteringInfoModal bind:showModal={showNetMeteringModal} />
{/if}

{#if featureId === 'cs' && showCloneSettings}
	<CopySettingsOnboardingWizard bind:showModal={showCloneSettings} />
{/if}

{#if featureId === 'pvs'}
	<PVSetupConfig
		type="Parallel"
		bind:showModal={showPVSetupModal}
		on:save={async (e) => {
			await save_home('net_metering_type', e.detail);
			push('/cons/pv_set_up');
		}}
	/>
{/if}
